<script setup>
import { useRoute, useRouter, RouterView } from 'vue-router'
import { computed, ref, watch, onMounted, onUnmounted, nextTick } from 'vue'
import { useStore } from 'vuex'
import { useDisplay } from 'vuetify'
import { systemMonitorgetHealthCheck } from '@/api/cfes/systemMonitoring'
import { supportBotAskStream } from '@/api/cfes/supportBot'
import { navigateToDefaultRoute } from '@/router'
import { toast } from '@/utils/toast'
import { endSession, startSessionKeeper, stopSessionKeeper } from '@/utils/session'
import cfesSupportAvatar from '@/assets/cfes-support.png'
import { marked } from 'marked'
import DOMPurify from 'dompurify'

marked.setOptions({ gfm: true, breaks: true })

const renderMarkdown = (text) => {
  const raw = String(text ?? '')
  if (!raw) return ''
  try {
    const html = marked.parse(raw)
    return DOMPurify.sanitize(html)
  } catch (e) {
    console.warn('[supportBot] markdown render failed', e)
    return DOMPurify.sanitize(raw)
  }
}

const { mobile } = useDisplay()

const route = useRoute()
const router = useRouter()
const store = useStore()
const activeName = computed(() => route.name || null)

const brand = computed({
  get: () => store.getters.brand,
  set: (val) => updateBrandAndRoutes(val)
})

const region = computed({
  get: () => store.getters.region,
  set: (val) => store.commit('SET_SELECTED_REGION', val),
})

const showLogoutDialog = ref(false)

// ----- Support Bot -----
const showSupportBot = ref(false)
const botInput = ref('')
const botLoading = ref(false)
const botSidebarOpen = ref(true)
// localStorage keys (kept here so they're easy to find/rotate together).
// v2 = multi-conversation layout. v1 keys are kept only for one-time migration.
const BOT_LS_CONVERSATIONS = 'cfes.supportBot.conversations.v2'
const BOT_LS_ACTIVE_ID = 'cfes.supportBot.activeId.v2'
const BOT_LS_LEGACY_MESSAGES = 'cfes.supportBot.messages.v1'
const BOT_LS_LEGACY_SESSION = 'cfes.supportBot.sessionId.v1'
// Cap stored history so localStorage doesn't grow without bound.
const BOT_HISTORY_MAX = 100
const BOT_CONVERSATIONS_MAX = 30

const isSessionExpiredMessage = (text) => String(text || '').toLowerCase().includes('session expired')
// When true, the watcher below is allowed to write conversations to localStorage.
// We flip this off on session-expired so the transient "Session expired" bubble
// and the reset greeting are NOT persisted over the user's real history.
// Important: we intentionally do NOT wipe existing persisted conversations here
// — those belong to the user and must survive a logout/login round-trip.
const botPersistenceEnabled = ref(true)

const disableBotPersistenceOnSessionExpired = (text) => {
  if (!isSessionExpiredMessage(text)) return false
  botPersistenceEnabled.value = false
  return true
}

// Session-expired handler: purge bot state, then trigger a full app logout
// (same flow as the topbar Logout button). Guarded so we only kick it off once
// per page life-cycle even if multiple SSE frames / errors mention it.
const sessionExpiredHandled = ref(false)
const handleSessionExpiredAndLogout = (text) => {
  if (!isSessionExpiredMessage(text)) return false
  if (sessionExpiredHandled.value) return true
  sessionExpiredHandled.value = true
  disableBotPersistenceOnSessionExpired(text)
  // Also reset the in-memory active conversation so the user doesn't see the
  // stale error/"No response received" bubble while the redirect is in flight.
  try {
    const conv = activeConversation.value
    if (conv) {
      conv.messages = [defaultGreeting()]
      conv.sessionId = ''
      conv.title = 'New chat'
      conv.updatedAt = Date.now()
    }
  } catch { /* ignore */ }
  try { toast.warning('Session expired. Redirecting to login...') } catch { /* ignore */ }
  // Fire-and-forget: dispatch LogOut then navigate to /login.
  endSession()
  return true
}

const defaultGreeting = () => ({
  role: 'bot',
  text: `Welcome! I'm Fodi, your CFES Support Assistant.

You can ask me questions or tell me about any issues you run into.

To help you faster, please include the following details when reporting a problem:
- Environment (e.g. PreProd/Prod)
- Brand (e.g. VW, Audi, Skoda)
- Region (e.g. EU / NAR)
- VIN
- Time range (e.g. 2026-06-05 00:00 -> 03:05 UTC)
- Error message

I'll do my best to support you and find a solution.`,
})

const genConvId = () => `c_${Date.now().toString(36)}_${Math.random().toString(36).slice(2, 8)}`

const createConversation = () => ({
  id: genConvId(),
  title: 'New chat',
  sessionId: '',
  messages: [defaultGreeting()],
  createdAt: Date.now(),
  updatedAt: Date.now(),
})

const deriveTitleFromMessages = (msgs) => {
  if (!Array.isArray(msgs)) return 'New chat'
  const firstUser = msgs.find((m) => m && m.role === 'user' && m.text)
  if (firstUser) {
    const t = String(firstUser.text).trim().replace(/\s+/g, ' ')
    return t.length > 40 ? `${t.slice(0, 40)}…` : t
  }
  return 'New chat'
}

const isLegacyGreeting = (msg) => {
  if (!msg || typeof msg !== 'object') return false
  return msg.role === 'bot' && String(msg.text || '').includes('Hi! I am the CFES support bot.')
}

// One-time migration from v1 single-conversation storage to v2 list.
const migrateLegacyConversation = () => {
  try {
    const raw = localStorage.getItem(BOT_LS_LEGACY_MESSAGES)
    if (!raw) return null
    const parsed = JSON.parse(raw)
    if (!Array.isArray(parsed) || parsed.length === 0) return null
    if (parsed.length === 1 && isLegacyGreeting(parsed[0])) return null
    const sessionId = localStorage.getItem(BOT_LS_LEGACY_SESSION) || ''
    return {
      id: genConvId(),
      title: deriveTitleFromMessages(parsed),
      sessionId,
      messages: parsed,
      createdAt: Date.now(),
      updatedAt: Date.now(),
    }
  } catch (e) {
    return null
  }
}

const loadConversations = () => {
  try {
    const raw = localStorage.getItem(BOT_LS_CONVERSATIONS)
    if (raw) {
      const parsed = JSON.parse(raw)
      if (Array.isArray(parsed) && parsed.length > 0) return parsed
    }
    const migrated = migrateLegacyConversation()
    if (migrated) {
      // Best-effort cleanup of legacy keys so we don't migrate twice.
      try {
        localStorage.removeItem(BOT_LS_LEGACY_MESSAGES)
        localStorage.removeItem(BOT_LS_LEGACY_SESSION)
      } catch (e) { /* ignore */ }
      return [migrated]
    }
  } catch (e) {
    console.warn('[supportBot] failed to load conversations', e)
  }
  return [createConversation()]
}

const loadActiveId = (list) => {
  try {
    const id = localStorage.getItem(BOT_LS_ACTIVE_ID)
    if (id && list.some((c) => c.id === id)) return id
  } catch (e) { /* ignore */ }
  return list[0]?.id || ''
}

const conversations = ref(loadConversations())
const activeConvId = ref(loadActiveId(conversations.value))
const botListRef = ref(null)

const activeConversation = computed(() =>
  conversations.value.find((c) => c.id === activeConvId.value) || conversations.value[0]
)

const botMessages = computed(() => activeConversation.value?.messages || [])

// Persist conversations + active id.
watch(
  conversations,
  (val) => {
    if (!botPersistenceEnabled.value) return
    try {
      // Cap per-conversation history and total number of conversations.
      const trimmed = val.slice(-BOT_CONVERSATIONS_MAX).map((c) => ({
        ...c,
        messages: Array.isArray(c.messages) && c.messages.length > BOT_HISTORY_MAX
          ? c.messages.slice(-BOT_HISTORY_MAX)
          : c.messages,
      }))
      localStorage.setItem(BOT_LS_CONVERSATIONS, JSON.stringify(trimmed))
    } catch (e) {
      // localStorage may be full or unavailable (private mode); fail silently.
    }
  },
  { deep: true }
)

watch(activeConvId, (val) => {
  if (!botPersistenceEnabled.value) return
  try {
    if (val) localStorage.setItem(BOT_LS_ACTIVE_ID, val)
    else localStorage.removeItem(BOT_LS_ACTIVE_ID)
  } catch (e) { /* ignore */ }
})

// Sort conversations by recency for the sidebar without mutating the source array.
const sortedConversations = computed(() =>
  [...conversations.value].sort((a, b) => (b.updatedAt || 0) - (a.updatedAt || 0))
)

const newConversation = () => {
  // If the current active conversation is still empty (only greeting), reuse it
  // to avoid piling up empty "New chat" entries.
  const current = activeConversation.value
  if (current && current.messages.length <= 1) {
    activeConvId.value = current.id
    botInput.value = ''
    scrollBotToBottom()
    return
  }
  const conv = createConversation()
  conversations.value.push(conv)
  activeConvId.value = conv.id
  botInput.value = ''
  scrollBotToBottom()
}

const selectConversation = (id) => {
  if (id === activeConvId.value) return
  activeConvId.value = id
  botInput.value = ''
  scrollBotToBottom()
}

const deleteConversation = (id) => {
  const idx = conversations.value.findIndex((c) => c.id === id)
  if (idx < 0) return
  conversations.value.splice(idx, 1)
  if (conversations.value.length === 0) {
    const conv = createConversation()
    conversations.value.push(conv)
    activeConvId.value = conv.id
  } else if (activeConvId.value === id) {
    activeConvId.value = sortedConversations.value[0].id
  }
}

// Backend returns envelope { code, msg } where msg is a BedrockSupportAnswerResponse.
// It can arrive as a JSON object or as a stringified JSON; normalize to an object.
const parseBotPayload = (msg) => {
  if (!msg) return null
  if (typeof msg === 'object') return msg
  if (typeof msg === 'string') {
    try {
      return JSON.parse(msg)
    } catch (e) {
      return null
    }
  }
  return null
}

// Some backend responses append a Java object-style citation dump like:
// "... Citation(GeneratedResponsePart=..., RetrievedReferences=[...])"
// Keep only the human-readable answer part.
const stripCitationSuffix = (text) => {
  if (!text) return ''
  const value = String(text)
  const markerIndex = value.indexOf('Citation(')
  return markerIndex >= 0 ? value.slice(0, markerIndex).trim() : value.trim()
}

const scrollBotToBottom = async () => {
  await nextTick()
  const el = botListRef.value
  if (el) el.scrollTop = el.scrollHeight
}

const openSupportBot = () => {
  showSupportBot.value = true
  scrollBotToBottom()
}

const pushMessageToActive = (message) => {
  const conv = activeConversation.value
  if (!conv) return
  conv.messages.push(message)
  conv.updatedAt = Date.now()
  if (conv.title === 'New chat' && message.role === 'user') {
    conv.title = deriveTitleFromMessages(conv.messages)
  }
}

const sendBotQuestion = async () => {
  const question = botInput.value.trim()
  if (!question || botLoading.value) return
  const conv = activeConversation.value
  if (!conv) return
  pushMessageToActive({ role: 'user', text: question })
  botInput.value = ''
  botLoading.value = true
  scrollBotToBottom()

  // Capture id at send time so a late response is routed to the originating conversation.
  const originatingId = conv.id
  const findOriginating = () => conversations.value.find((c) => c.id === originatingId)
  const pushToOriginating = (message) => {
    const target = findOriginating()
    if (!target) return
    target.messages.push(message)
    target.updatedAt = Date.now()
  }

  try {
    let answerReceived = false
    let sawError = false

    await supportBotAskStream({
      question,
      sessionId: conv.sessionId || undefined,
      onEvent: ({ event, data }) => {
        if (event === 'heartbeat') return // keep-alive only

        if (event === 'answer') {
          answerReceived = true
          // Data may already be a parsed object (BedrockSupportAnswerResponse)
          // or a JSON string if for some reason parsing failed upstream.
          const payload = parseBotPayload(data)
          const target = findOriginating()
          if (target && payload?.sessionId) target.sessionId = payload.sessionId
          const answer = stripCitationSuffix(payload?.answer) || '(empty answer)'
          pushToOriginating({ role: 'bot', text: answer })
          scrollBotToBottom()
        } else if (event === 'error') {
          sawError = true
          const errText =
            (data && (data.message || data.msg)) ||
            (typeof data === 'string' ? data : 'Support bot returned an error.')
          // Session-expired takes over the flow (redirects to /login). We
          // already marked `sawError` so the post-stream fallback won't push
          // a stale "No response received" bubble on top of it.
          if (handleSessionExpiredAndLogout(errText)) return
          pushToOriginating({ role: 'bot', text: errText, error: true })
        } else if (event === 'done') {
          // Stream is closing normally; nothing to render.
        }
      },
    })

    if (!answerReceived && !sawError) {
      // If session-expired handling has already fired for this page, the
      // redirect is in flight — do not push any bubble (nor probe again).
      if (sessionExpiredHandled.value) return

      // Stream ended without any answer/error frame — treat as failure.
      // This most commonly happens when the SSO session expires mid-stream:
      // the backend silently closes the SSE emitter without sending an
      // `event: error` frame. Do a lightweight health check — if the session
      // is truly gone, the axios interceptor in `@/api/request` will detect
      // the 401/403 and trigger LogOut automatically. If the check succeeds,
      // it was a genuine "no response" and we surface the generic message.
      let sessionAlive = true
      try {
        await systemMonitorgetHealthCheck()
      } catch (probeErr) {
        const st = probeErr?.status || probeErr?.response?.status
        const probeMsg = String(probeErr?.message || '').toLowerCase()
        // The axios interceptor rejects with `new Error('Session expired')`
        // (no status field) when it detects `code === 401` in a 200-OK body,
        // so we also match on the message text.
        const looksLikeAuth = st === 401 || st === 403 || probeMsg.includes('session expired')
        if (looksLikeAuth) {
          sessionAlive = false
          handleSessionExpiredAndLogout('session expired')
        }
      }
      // Even if the probe succeeded, if the interceptor kicked off logout
      // concurrently we would have flipped this flag — respect it.
      if (sessionAlive && !sessionExpiredHandled.value) {
        pushToOriginating({
          role: 'bot',
          text: 'No response received from the support bot. Please try again.',
          error: true,
        })
      }
    }
  } catch (err) {
    console.error('Support bot error', err)
    const errMsg = err?.msg || err?.message || 'Sorry, something went wrong. Please try again later.'
    if (
      handleSessionExpiredAndLogout(errMsg) ||
      err?.status === 401 ||
      err?.status === 403 ||
      /session expired/i.test(errMsg)
    ) {
      // Logout flow will take over; skip pushing a stale error bubble.
      return
    }
    pushToOriginating({ role: 'bot', text: errMsg, error: true })
  } finally {
    botLoading.value = false
    scrollBotToBottom()
  }
}

const clearBotChat = () => {
  const conv = activeConversation.value
  if (!conv) return
  conv.messages = [defaultGreeting()]
  // Start a fresh server-side session so old context is dropped.
  conv.sessionId = ''
  conv.title = 'New chat'
  conv.updatedAt = Date.now()
}

// When switching conversations, jump scroll to the bottom of the new one.
watch(activeConvId, () => scrollBotToBottom())

const formatConvTime = (ts) => {
  if (!ts) return ''
  const d = new Date(ts)
  const now = new Date()
  const sameDay = d.toDateString() === now.toDateString()
  if (sameDay) {
    return d.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })
  }
  return d.toLocaleDateString([], { month: 'short', day: '2-digit' })
}

// Helper to update brand and regenerate routes
const updateBrandAndRoutes = async (brandValue) => {
  store.commit('SET_SELECTED_BRAND', brandValue)
  await store.dispatch('GenerateRoutes')
  
  // Check if current route is still valid after reloading routes
  const currentRouteName = route.name
  if (currentRouteName && !router.hasRoute(currentRouteName)) {
    console.warn('[layout] Current route no longer available after brand change, redirecting to default page')
    navigateToDefaultRoute(router)
  }
}

const confirmLogout = async () => {
  await endSession()
}

const logout = () => {
  showLogoutDialog.value = true
}

// Menu metadata - defines display properties for all possible routes
// This is static configuration that maps route names to their UI representation
const menuMetadata = {
  'monitor': { label: 'System monitoring', icon: 'mdi-monitor-dashboard' },
  'business-log': { label: 'Business monitoring', icon: 'mdi-clipboard-list-outline' },
  'trace-monitoring': { label: 'Trace monitoring', icon: 'mdi-timeline-text-outline' },
  'vehicle-monitor': { label: 'Vehicle monitoring', icon: 'mdi-car' },
  'vehicle-extId-monitor': { label: 'Vehicle ExtId monitoring', icon: 'mdi-car-search' },
  'mbc-pms': { label: 'MBC & PMS', icon: 'mdi-database' },
  'queue-monitor': { label: 'Queue monitoring', icon: 'mdi-transit-connection-variant' },
  'sales-info': { label: 'Sales information', icon: 'mdi-chart-bar' },
  'test-data-admin': { label: 'Test data admin', icon: 'mdi-test-tube' },
  'vin-tenant-config': { label: 'VIN Tenant Config', icon: 'mdi-car-cog' },
  'api-config': { label: 'API config', icon: 'mdi-cog-outline' },
  'data-insight': { label: 'Data Insight', icon: 'mdi-chart-line' },
  'user-management': { label: 'User management', icon: 'mdi-account-multiple-outline', group: 'admin' },
  'role-management': { label: 'Role management', icon: 'mdi-account-key-outline', group: 'admin' },
  'menu-management': { label: 'Menu management', icon: 'mdi-view-list-outline', group: 'admin' },
}

// Dynamically compute available routes based on what's loaded in the router
// This allows the menu to update automatically when routes are added/removed based on user permissions
const availableRoutes = computed(() => {
  const cachedRoutes = store.getters.cachedRoutes
  return cachedRoutes
    .filter(r => r.name && menuMetadata[r.name])
    .map(r => r.name)
})

// Grouped navigation items (e.g., System admin expandable section)
// Only shows if user has access to admin routes
const sysAdmSubMenuItems = computed(() => {
  const adminItems = availableRoutes.value
    .filter(name => menuMetadata[name].group === 'admin')
    .map(name => ({ name, ...menuMetadata[name] }))
  
  return adminItems.length > 0 ? [{
    key: 'admin',
    title: 'System admin',
    icon: 'mdi-shield-account-outline',
    items: adminItems,
  }] : []
})

// Flat navigation items (non-grouped menu items)
// Automatically filters to only show routes the user has access to
const flatMenuItems = computed(() => {
  return availableRoutes.value
    .filter(name => !menuMetadata[name].group)
    .map(name => ({ name, ...menuMetadata[name] }))
})

const navOpen = ref({ admin: false })

watch(
  () => activeName.value,
  (val) => {
    // Only check admin group if it exists (user may not have admin permissions)
    if (sysAdmSubMenuItems.value.length > 0) {
      navOpen.value.admin = sysAdmSubMenuItems.value[0].items.some((i) => i.name === val)
    }
  },
  { immediate: true }
)

const username = computed(() => store.getters.nickName)
const serviceVersion = computed(() => store.getters.serviceVersion || '')
const envLabel = ref('')

const supportBotEnabled = computed(
  () => String(import.meta.env.VITE_FEATURE_SUPPORT_BOT ?? 'true').toLowerCase() !== 'false'
)

const deriveEnv = (confList) => {
  if (!Array.isArray(confList)) return ''
  for (let i = 0; i < confList.length; i += 1) {
    const entry = confList[i]
    if (Array.isArray(entry) && entry[0] === 'spring.config.activate.on-profile') {
      const profile = entry[1] || ''
      if (profile.endsWith('dev')) return 'TUI'
      if (profile.endsWith('app')) return 'APPROVAL'
      if (profile.endsWith('prd')) return 'Production'
      return profile
    }
  }
  return 'IT'
}

onMounted(async () => {
  try {
    const res = await systemMonitorgetHealthCheck()
    const conf = res?.ApplicationConfiguration
    envLabel.value = deriveEnv(conf)
  } catch (err) {
    console.warn('Fetch env failed', err)
    envLabel.value = 'IT'
  }

  startSessionKeeper()
})

onUnmounted(() => {
  stopSessionKeeper()
})

const regionItems = computed(() => store.getters.regionItems || [])
const brandItemsMap = computed(() => store.getters.brandItemsMap || {})
const brandOptions = computed(() => brandItemsMap.value[region.value] || [])

// Apply defaults when backend data arrives
watch(
  () => ({ region: region.value, brand: brand.value, regions: regionItems.value, defaults: { dr: store.getters.defaultRegion, db: store.getters.defaultBrand } }),
  () => {
    if (!region.value) {
      const fallbackRegion = store.getters.defaultRegion || regionItems.value?.[0]?.value
      if (fallbackRegion) {
        store.commit('SET_SELECTED_REGION', fallbackRegion)
        // GenerateRoutes will be called by the region watcher below
      }
    }
    if (!brand.value) {
      const regionKey = store.getters.region || store.getters.defaultRegion
      const fallbackBrand = brandItemsMap.value[regionKey]?.[0]?.value || store.getters.defaultBrand
      if (fallbackBrand) {
        console.log('Setting default brand and regenerating routes')
        updateBrandAndRoutes(fallbackBrand)
      }
    }
  },
  { immediate: true, deep: true }
)

// keep brand in sync when region changes
watch(
  () => region.value,
  (val) => {
    const first = brandItemsMap.value[val]?.[0]?.value
    if (first) updateBrandAndRoutes(first)
  },
  { immediate: true }
)
</script>

<template>
  <div class="shell">
    <header class="topbar">
      <h2 class="topbar-title">CFES admin tool<span v-if="envLabel"> - {{ envLabel }}</span></h2>
      <div class="topbar-actions">
        <v-btn
          v-if="supportBotEnabled"
          size="small"
          variant="tonal"
          color="primary"
          class="support-bot-btn"
          @click="openSupportBot"
        >
          <v-avatar size="22" class="mr-2 support-bot-btn-avatar">
            <v-img :src="cfesSupportAvatar" alt="Fodi" />
          </v-avatar>
          Fodi - CFES Support Bot
        </v-btn>
        <span class="username" v-if="username">{{ username }}</span>
        <v-btn size="small" variant="text" color="primary" class="logout-btn" @click="logout">Logout</v-btn>
      </div>
    </header>

    <aside class="sidebar">
      <v-list density="compact" nav class="sidebar-list">
        <!-- System admin group - only shown if user has admin permissions -->
        <v-list-group
          v-if="sysAdmSubMenuItems.length > 0"
          v-model="navOpen.admin"
          prepend-icon="mdi-shield-account-outline"
          expand-icon="mdi-chevron-down"
          collapse-icon="mdi-chevron-up"
        >
          <template #activator="{ props }">
            <v-list-item v-bind="props" title="System admin" class="group-activator" />
          </template>
          <v-list-item
            v-for="item in sysAdmSubMenuItems[0].items"
            :key="item.name"
            class="sub-item"
            :active="activeName === item.name"
            :prepend-icon="item.icon || sysAdmSubMenuItems[0].icon"
            :to="{ name: item.name }"
          >
            <v-list-item-title>{{ item.label }}</v-list-item-title>
          </v-list-item>
        </v-list-group>

        <v-list-item
          v-for="item in flatMenuItems"
          :key="item.name"
          :active="activeName === item.name"
          :prepend-icon="item.icon"
          :to="{ name: item.name }"
        >
          <v-list-item-title>{{ item.label }}</v-list-item-title>
        </v-list-item>

        <!-- Region & Brand selects inline after menu items -->
        <div class="inline-selects">
          <v-select
            v-model="region"
            :items="regionItems"
            item-title="text"
            item-value="value"
            label="Region"
            density="compact"
            variant="outlined"
            hide-details
          />
          <v-select
            v-model="brand"
            :items="brandOptions"
            item-title="text"
            item-value="value"
            label="Brand"
            density="compact"
            variant="outlined"
            hide-details
          />
        </div>
      </v-list>

      <div class="version-badge">V{{ serviceVersion || 'N/A' }}</div>
    </aside>

    <main class="content-area">
      <div class="content-inner">
        <RouterView />
      </div>
    </main>

    <v-dialog v-model="showLogoutDialog" max-width="360">
      <v-card>
        <v-card-title class="text-h6">Confirm logout</v-card-title>
        <v-card-text>Are you sure you want to logout?</v-card-text>
        <v-card-actions>
          <v-spacer />
          <v-btn variant="text" @click="showLogoutDialog = false">Cancel</v-btn>
          <v-btn color="primary" variant="flat" @click="confirmLogout">OK</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Support Bot Dialog -->
    <v-dialog v-if="supportBotEnabled" v-model="showSupportBot" :max-width="mobile ? '96vw' : '1100'">
      <v-card class="bot-card">
        <v-card-title class="d-flex align-center bot-title">
          <v-btn
            icon
            size="small"
            variant="text"
            class="mr-1"
            :title="botSidebarOpen ? 'Hide conversations' : 'Show conversations'"
            @click="botSidebarOpen = !botSidebarOpen"
          >
            <v-icon>{{ botSidebarOpen ? 'mdi-menu-open' : 'mdi-menu' }}</v-icon>
          </v-btn>
          <v-avatar size="32" class="mr-2">
            <v-img :src="cfesSupportAvatar" alt="Fodi" />
          </v-avatar>
          <span class="bot-title-text">Fodi - CFES Support Bot</span>
          <span v-if="activeConversation" class="bot-title-sub">— {{ activeConversation.title }}</span>
          <v-spacer />
          <v-btn icon="mdi-broom" size="small" variant="text" title="Clear current chat" @click="clearBotChat" />
          <v-btn icon="mdi-close" size="small" variant="text" @click="showSupportBot = false" />
        </v-card-title>
        <v-divider />

        <div class="bot-body">
          <aside v-if="botSidebarOpen" class="bot-sidebar">
            <div class="bot-sidebar-header">
              <v-btn
                block
                size="small"
                color="primary"
                variant="tonal"
                prepend-icon="mdi-plus"
                @click="newConversation"
              >
                New chat
              </v-btn>
            </div>
            <div class="bot-conv-list">
              <div
                v-for="conv in sortedConversations"
                :key="conv.id"
                class="bot-conv-item"
                :class="{ 'is-active': conv.id === activeConvId }"
                @click="selectConversation(conv.id)"
              >
                <v-icon size="16" class="bot-conv-icon">mdi-message-text-outline</v-icon>
                <div class="bot-conv-meta">
                  <div class="bot-conv-title" :title="conv.title">{{ conv.title }}</div>
                  <div class="bot-conv-time">{{ formatConvTime(conv.updatedAt) }}</div>
                </div>
                <v-btn
                  icon="mdi-trash-can-outline"
                  size="x-small"
                  variant="text"
                  class="bot-conv-delete"
                  title="Delete conversation"
                  @click.stop="deleteConversation(conv.id)"
                />
              </div>
            </div>
          </aside>

          <div class="bot-main">
            <div ref="botListRef" class="bot-messages">
              <div
                v-for="(m, idx) in botMessages"
                :key="idx"
                class="bot-msg-row"
                :class="m.role === 'user' ? 'is-user' : 'is-bot'"
              >
                <v-avatar v-if="m.role === 'bot'" size="36" class="bot-avatar">
                  <v-img :src="cfesSupportAvatar" alt="Fodi" />
                </v-avatar>
                <div class="bot-msg-wrap">
                  <div
                    v-if="m.role === 'bot' && !m.error"
                    class="bot-msg-bubble bot-msg-markdown"
                    v-html="renderMarkdown(m.text)"
                  ></div>
                  <div v-else class="bot-msg-bubble" :class="{ 'is-error': m.error }">{{ m.text }}</div>
                </div>
                <v-avatar v-if="m.role === 'user'" size="36" class="user-avatar" color="primary">
                  <v-icon color="white">mdi-account</v-icon>
                </v-avatar>
              </div>
              <div v-if="botLoading" class="bot-msg-row is-bot">
                <v-avatar size="36" class="bot-avatar">
                  <v-img :src="cfesSupportAvatar" alt="Fodi" />
                </v-avatar>
                <div class="bot-msg-wrap">
                  <div class="bot-msg-bubble bot-thinking">
                    <v-progress-circular indeterminate size="14" width="2" class="mr-2" />
                    Thinking...
                  </div>
                </div>
              </div>
            </div>

            <v-divider />
            <v-card-actions class="bot-input-row">
              <v-textarea
                v-model="botInput"
                placeholder="Ask a question about CFES... (Press Enter to send, Shift+Enter for newline)"
                variant="outlined"
                hide-details
                auto-grow
                rows="3"
                max-rows="6"
                :disabled="botLoading"
                class="bot-input-field"
                @keydown.enter.exact.prevent="sendBotQuestion"
              />
              <v-btn
                color="primary"
                variant="flat"
                class="ml-2 bot-send-btn"
                :loading="botLoading"
                :disabled="!botInput.trim()"
                @click="sendBotQuestion"
              >
                Send
              </v-btn>
            </v-card-actions>
          </div>
        </div>
      </v-card>
    </v-dialog>
  </div>
</template>

<style scoped>
.shell {
  display: grid;
  grid-template-rows: 56px 1fr;
  grid-template-columns: 240px 1fr;
  width: 100vw;
  height: 100vh;
  margin: 0;
  padding: 0;
  overflow: hidden;
  background: #eef1f5;
}

.topbar {
  grid-column: 1 / span 2;
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 0 18px;
  background: #ffffff;
  border-bottom: 1px solid #e5e7eb;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.04);
  z-index: 2;
}

.topbar-title {
  font-size: 17px;
  font-weight: 650;
  margin: 0;
  color: #0f172a;
}

.topbar-title span {
  font-weight: 600;
  margin-left: 8px;
  color: #4b5563;
}

.topbar-actions {
  display: flex;
  align-items: center;
}

.username {
  margin-right: 16px;
  font-size: 14px;
  color: #4b5563;
}

.logout-btn {
  margin-left: 8px;
}

.support-bot-btn {
  margin-right: 12px;
}

.sidebar {
  position: relative;
  display: flex;
  flex-direction: column;
  height: 100%;
  border-right: 1px solid #e5e7eb;
  background-color: #f9fafb;
  padding: 0 0 40px;
  box-sizing: border-box;
  overflow: hidden;
}

.sidebar-list {
  flex: 1;
  padding: 8px 0 0;
  margin: 0;
  overflow-y: auto;
}

.sidebar-list :deep(.v-list-item) {
  margin: 4px 10px;
  border-radius: 10px;
  padding-inline-start: 14px;
  padding-inline-end: 14px;
}

.sidebar-list :deep(.v-list-item:hover) {
  background: #e8f1ff;
}

.sidebar-list :deep(.v-list-item--active) {
  background: #dbeafe;
  color: #0f172a;
  font-weight: 600;
}

.sidebar-list :deep(.v-list-item-title) {
  padding: 6px 0;
}

.sidebar-list :deep(.v-list-group__header) {
  margin: 4px 10px;
  border-radius: 10px;
  padding-inline-start: 14px;
  padding-inline-end: 14px;
}

.sidebar-list :deep(.group-activator) {
  margin: 4px 10px;
  border-radius: 10px;
  padding-inline-start: 14px;
  padding-inline-end: 14px;
}

.sidebar-list :deep(.v-list-group__header:hover) {
  background: #e8f1ff;
}

.content-area {
  height: 100%;
  overflow-y: auto;
}

.content-inner {
  min-height: 100%;
  padding: 24px 28px;
  background: #f5f7fb;
}

.version-badge {
  position: absolute;
  left: 16px;
  bottom: 14px;
  padding: 6px 10px;
  border-radius: 10px;
  background: rgba(15, 23, 42, 0.88);
  color: #e2e8f0;
  font-size: 12px;
  letter-spacing: 0.2px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.12);
  pointer-events: none;
  user-select: none;
}

.inline-selects {
  display: grid;
  gap: 10px;
  margin: 12px 14px 8px;
  padding: 10px 12px;
  background: #ffffff;
  border: 1px solid #e5e7eb;
  border-radius: 12px;
  box-shadow: 0 4px 10px rgba(0, 0, 0, 0.05);
}

.sidebar-list :deep(.v-list-group__items) {
  padding-left: 0;
  margin-left: 0;
}

.sidebar-list :deep(.sub-item) {
  padding-inline-start: 0;
  padding-inline-end: 10px;
  padding-left: 36px !important;
}

.sidebar-list :deep(.v-list-item__prepend) {
  margin-inline-end: 8px;
}

.sidebar-list :deep(.v-list-item__spacer) {
  width: 4px;
}

.bot-card {
  display: flex;
  flex-direction: column;
  width: 1100px;
  max-width: 96vw;
  height: 86vh !important;
  min-height: 86vh;
  max-height: 86vh;
  overflow: hidden;
}

.bot-title {
  font-size: 15px;
  font-weight: 600;
  padding: 12px 16px;
  flex-shrink: 0;
}

.bot-title-text {
  white-space: nowrap;
}

.bot-title-sub {
  margin-left: 8px;
  font-weight: 400;
  font-size: 13px;
  color: #64748b;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  max-width: 360px;
}

.bot-body {
  display: flex;
  flex: 1;
  min-height: 0;
  background: #f8fafc;
}

.bot-sidebar {
  width: 240px;
  flex-shrink: 0;
  display: flex;
  flex-direction: column;
  border-right: 1px solid #e5e7eb;
  background: #ffffff;
  min-height: 0;
}

.bot-sidebar-header {
  padding: 10px 12px;
  border-bottom: 1px solid #f1f5f9;
}

.bot-conv-list {
  flex: 1;
  overflow-y: auto;
  padding: 6px 6px 12px;
}

.bot-conv-item {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 8px 10px;
  margin: 2px 4px;
  border-radius: 8px;
  cursor: pointer;
  color: #1f2937;
  transition: background-color 0.15s ease;
}

.bot-conv-item:hover {
  background: #f1f5f9;
}

.bot-conv-item.is-active {
  background: #dbeafe;
  color: #0f172a;
}

.bot-conv-icon {
  flex-shrink: 0;
  color: #64748b;
}

.bot-conv-item.is-active .bot-conv-icon {
  color: #2563eb;
}

.bot-conv-meta {
  flex: 1;
  min-width: 0;
}

.bot-conv-title {
  font-size: 13px;
  font-weight: 500;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.bot-conv-time {
  font-size: 11px;
  color: #94a3b8;
  margin-top: 2px;
}

.bot-conv-delete {
  opacity: 0;
  flex-shrink: 0;
  transition: opacity 0.15s ease;
}

.bot-conv-item:hover .bot-conv-delete,
.bot-conv-item.is-active .bot-conv-delete {
  opacity: 1;
}

.bot-main {
  flex: 1;
  display: flex;
  flex-direction: column;
  min-width: 0;
  min-height: 0;
}

.bot-messages {
  flex: 1;
  overflow-y: auto;
  padding: 14px 16px;
  background: #f8fafc;
  min-height: 0;
}

.bot-msg-row {
  display: flex;
  align-items: flex-end;
  gap: 8px;
  margin-bottom: 10px;
}

.bot-msg-row.is-user {
  justify-content: flex-end;
}

.bot-msg-row.is-bot {
  justify-content: flex-start;
}

.bot-avatar,
.user-avatar {
  flex-shrink: 0;
  border: 1px solid #e5e7eb;
  background: #fff;
}

.user-avatar {
  border-color: transparent;
}

.bot-msg-wrap {
  display: flex;
  flex-direction: column;
  max-width: 72%;
}

.bot-msg-row.is-user .bot-msg-wrap {
  align-items: flex-end;
}

.bot-msg-row.is-bot .bot-msg-wrap {
  align-items: flex-start;
}

.bot-msg-bubble {
  max-width: 100%;
  padding: 8px 12px;
  border-radius: 12px;
  font-size: 13px;
  line-height: 1.5;
  white-space: pre-wrap;
  word-break: break-word;
  box-shadow: 0 1px 2px rgba(15, 23, 42, 0.06);
}

.bot-msg-row.is-user .bot-msg-bubble {
  background: #2563eb;
  color: #fff;
  border-bottom-right-radius: 2px;
}

.bot-msg-row.is-bot .bot-msg-bubble {
  background: #ffffff;
  color: #0f172a;
  border: 1px solid #e5e7eb;
  border-bottom-left-radius: 2px;
}

.bot-msg-bubble.is-error {
  background: #fef2f2;
  color: #b91c1c;
  border-color: #fecaca;
}

.bot-msg-markdown {
  white-space: normal;
}

.bot-msg-markdown :deep(p) {
  margin: 0 0 8px;
}
.bot-msg-markdown :deep(p:last-child) {
  margin-bottom: 0;
}
.bot-msg-markdown :deep(ul),
.bot-msg-markdown :deep(ol) {
  margin: 4px 0 8px;
  padding-left: 20px;
}
.bot-msg-markdown :deep(li) {
  margin: 2px 0;
}
.bot-msg-markdown :deep(h1),
.bot-msg-markdown :deep(h2),
.bot-msg-markdown :deep(h3),
.bot-msg-markdown :deep(h4),
.bot-msg-markdown :deep(h5),
.bot-msg-markdown :deep(h6) {
  margin: 10px 0 6px;
  font-weight: 650;
  line-height: 1.3;
}
.bot-msg-markdown :deep(h1) { font-size: 1.25em; }
.bot-msg-markdown :deep(h2) { font-size: 1.15em; }
.bot-msg-markdown :deep(h3) { font-size: 1.05em; }
.bot-msg-markdown :deep(h4),
.bot-msg-markdown :deep(h5),
.bot-msg-markdown :deep(h6) { font-size: 1em; }
.bot-msg-markdown :deep(a) {
  color: #2563eb;
  text-decoration: underline;
  word-break: break-all;
}
.bot-msg-markdown :deep(code) {
  background: #f1f5f9;
  color: #b91c1c;
  padding: 1px 5px;
  border-radius: 4px;
  font-family: ui-monospace, SFMono-Regular, Menlo, Consolas, monospace;
  font-size: 0.92em;
}
.bot-msg-markdown :deep(pre) {
  background: #0f172a;
  color: #e2e8f0;
  padding: 10px 12px;
  border-radius: 8px;
  overflow-x: auto;
  margin: 6px 0 10px;
  font-size: 12px;
  line-height: 1.5;
}
.bot-msg-markdown :deep(pre code) {
  background: transparent;
  color: inherit;
  padding: 0;
  border-radius: 0;
  font-size: inherit;
}
.bot-msg-markdown :deep(blockquote) {
  margin: 6px 0;
  padding: 4px 10px;
  border-left: 3px solid #cbd5e1;
  color: #475569;
  background: #f8fafc;
  border-radius: 4px;
}
.bot-msg-markdown :deep(table) {
  border-collapse: collapse;
  margin: 6px 0 10px;
  font-size: 12px;
  width: auto;
  max-width: 100%;
  display: block;
  overflow-x: auto;
}
.bot-msg-markdown :deep(th),
.bot-msg-markdown :deep(td) {
  border: 1px solid #e5e7eb;
  padding: 4px 8px;
  text-align: left;
}
.bot-msg-markdown :deep(th) {
  background: #f1f5f9;
  font-weight: 600;
}
.bot-msg-markdown :deep(hr) {
  border: none;
  border-top: 1px solid #e5e7eb;
  margin: 10px 0;
}
.bot-msg-markdown :deep(img) {
  max-width: 100%;
  border-radius: 6px;
}

.bot-thinking {
  display: inline-flex;
  align-items: center;
  color: #64748b;
  font-style: italic;
}

.bot-input-row {
  padding: 12px 14px;
  background: #fff;
  align-items: flex-end;
}

.bot-input-field {
  flex: 1;
}

.bot-input-field :deep(textarea) {
  font-size: 14px;
  line-height: 1.5;
  min-height: 72px;
}

.bot-send-btn {
  height: 44px;
  min-width: 88px;
}
</style>
